import { useEffect, useState } from 'react';
import {
  androidReleaseManifestUrl,
  fallbackAndroidRelease,
  parseAndroidReleaseManifest,
} from './release';
import type { AndroidRelease } from './release';

export type AndroidReleaseStatus = 'loading' | 'live' | 'fallback';

interface AndroidReleaseState {
  release: AndroidRelease;
  status: AndroidReleaseStatus;
}

export function useAndroidRelease(): AndroidReleaseState {
  const [state, setState] = useState<AndroidReleaseState>({
    release: fallbackAndroidRelease,
    status: 'loading',
  });

  useEffect(() => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8_000);
    let active = true;

    async function loadRelease() {
      try {
        const response = await fetch(androidReleaseManifestUrl, { signal: controller.signal });
        if (!response.ok) throw new Error(`Release manifest returned HTTP ${response.status}.`);
        const release = parseAndroidReleaseManifest(await response.json());
        if (active) setState({ release, status: 'live' });
      } catch {
        if (active) setState({ release: fallbackAndroidRelease, status: 'fallback' });
      } finally {
        window.clearTimeout(timeout);
      }
    }

    void loadRelease();
    return () => {
      active = false;
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, []);

  return state;
}
